import React, { useEffect, useRef, useState } from 'react';
import { FiDollarSign, FiAlertTriangle, FiCheckSquare, FiArrowRight, FiTrendingUp, FiUserX, FiCalendar, FiZap } from 'react-icons/fi';

const DarkProblemSection = () => {
  const [isVisible, setIsVisible] = useState({
    heading: false,
    cards: [],
    bottomBox: false
  });

  const sectionRef = useRef(null);
  const headingRef = useRef(null);
  const cardsRef = useRef([]);
  const bottomRef = useRef(null);

  // Problem cards data
  const problemCards = [
    {
      icon: <FiDollarSign size={28} />,
      title: 'Custom Enterprise Systems',
      description: 'Tailored ERP extensions and workflow platforms built to match your exact business processes and scale with your operations.',
      gradient: 'from-rose-500/20 to-orange-500/20',
      iconBg: 'bg-gradient-to-br from-rose-500/10 to-orange-500/10'
    },
    {
      icon: <FiAlertTriangle size={28} />,
      title: 'AI Automation & Decision Engines',
      description: 'Leverage intelligent models to automate decisions, optimize resources, and unlock predictive insights for smarter business outcomes.',
      gradient: 'from-amber-500/20 to-red-500/20',
      iconBg: 'bg-gradient-to-br from-amber-500/10 to-red-500/10'
    },
    {
      icon: <FiCheckSquare size={28} />,
      title: 'SaaS Platform Engineering',
      description: 'End-to-end development of scalable, multi-tenant SaaS products with secure architecture, seamless user management, and flexible billing.',
      gradient: 'from-emerald-500/20 to-teal-500/20',
      iconBg: 'bg-gradient-to-br from-emerald-500/10 to-teal-500/10'
    },
    {
      icon: <FiCheckSquare size={28} />,
      title: 'IoT & Edge Solutions',
      description: 'Connect devices and sensors to real-time systems, enabling visibility, control, and faster decision-making at the edge.',
      gradient: 'from-emerald-500/20 to-teal-500/20',
      iconBg: 'bg-gradient-to-br from-emerald-500/10 to-teal-500/10'
    },
    {
      icon: <FiCheckSquare size={28} />,
      title: 'Data Platforms & Intelligence',
      description: 'Transform raw data into actionable insights with structured pipelines, analytics dashboards, and governance frameworks.',
      gradient: 'from-emerald-500/20 to-teal-500/20',
      iconBg: 'bg-gradient-to-br from-emerald-500/10 to-teal-500/10'
    },
    {
      icon: <FiCheckSquare size={28} />,
      title: 'Cloud-Native & DevSecOps',
      description: 'Accelerate delivery with secure, automated CI/CD pipelines, containerized environments, and resilient cloud infrastructure.',
      gradient: 'from-emerald-500/20 to-teal-500/20',
      iconBg: 'bg-gradient-to-br from-emerald-500/10 to-teal-500/10'
    },
  ];

  // Stats data for additional visual interest
  const stats = [
    { value: '25+', label: 'Empowering businesses from multiple sectors with smart technology', icon: <FiUserX size={16} /> },
    { value: '95%', label: 'Loyalty earned through performance and reliability', icon: <FiTrendingUp size={16} /> },
    { value: '10+', label: 'Delivering excellence through years of expertise', icon: <FiCalendar size={16} /> }
  ];

  // Intersection Observer for scroll animations
  useEffect(() => {
    const observerOptions = {
      threshold: 0.2,
      rootMargin: '0px 0px -50px 0px'
    };

    const headingObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setIsVisible(prev => ({ ...prev, heading: true }));
          headingObserver.disconnect();
        }
      });
    }, observerOptions);

    const cardObservers = cardsRef.current.map((card, index) => {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            setIsVisible(prev => ({
              ...prev,
              cards: [...prev.cards, index]
            }));
            observer.disconnect();
          }
        });
      }, { ...observerOptions, threshold: 0.1 });

      if (card) observer.observe(card);
      return observer;
    });

    const bottomObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setIsVisible(prev => ({ ...prev, bottomBox: true }));
          bottomObserver.disconnect();
        }
      });
    }, observerOptions);

    if (headingRef.current) headingObserver.observe(headingRef.current);
    if (bottomRef.current) bottomObserver.observe(bottomRef.current);

    return () => {
      headingObserver.disconnect();
      cardObservers.forEach(observer => observer.disconnect());
      bottomObserver.disconnect();
    };
  }, []);

  return (
    <section ref={sectionRef} className="bg-black text-white py-24 px-4 md:px-8 font-sans relative overflow-hidden">
      {/* Animated Background Gradients */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 -left-1/4 w-[500px] h-[500px] bg-emerald-500/10 rounded-full blur-[120px] animate-pulse"></div>
        <div className="absolute bottom-0 -right-1/4 w-[600px] h-[600px] bg-cyan-500/10 rounded-full blur-[120px] animate-pulse delay-1000"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-purple-500/5 rounded-full blur-[150px]"></div>
      </div>

      <div className="max-w-7xl mx-auto relative z-10">

        {/* Small Badge with animation */}
        <div className={`flex justify-center mb-6 transition-all duration-700 delay-200 transform ${isVisible.heading ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}`}>
          <div className="inline-flex items-center gap-2 bg-emerald-500/10 backdrop-blur-sm px-4 py-2 rounded-full border border-emerald-500/30">
            <FiZap className="text-emerald-400 w-4 h-4 animate-pulse" />
            <span className="text-emerald-400 text-xs font-bold tracking-[2px] uppercase">SOUND FAMILIAR?</span>
          </div>
        </div>

        {/* Main Heading with staggered animation */}
        <div
          ref={headingRef}
          className={`transition-all duration-700 delay-300 transform ${isVisible.heading ? 'translate-y-0 opacity-100' : 'translate-y-12 opacity-0'}`}
        >
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-extrabold text-center leading-tight tracking-tight max-w-5xl mx-auto mb-8">
            We specialize in
            <span className="relative inline-block mx-2">
              <span className="text-emerald-400 bg-gradient-to-r from-emerald-400 to-cyan-400 bg-clip-text text-transparent">
                building software systems from scratch,
              </span>
              <svg className="absolute -bottom-2 left-0 w-full h-2" viewBox="0 0 200 8" fill="none">
                <path d="M0 4 L200 4" stroke="url(#gradient)" strokeWidth="2" strokeDasharray="6 6" />
                <defs>
                  <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="#10b981" />
                    <stop offset="100%" stopColor="#06b6d4" />
                  </linearGradient>
                </defs>
              </svg>
            </span>
            <br />
            <span className="bg-gradient-to-r from-white to-gray-400 bg-clip-text text-transparent">
              tailored specifically to your business operations rather than relying on generic, off-the-shelf solutions.
            </span>
          </h2>
        </div>

        {/* Sub Text with animation */}
        <div className={`transition-all duration-700 delay-500 transform ${isVisible.heading ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}`}>
          <p className="text-gray-400 text-center text-sm md:text-lg font-medium max-w-2xl mx-auto leading-relaxed mb-16">
            We serve as a strategic partner to global organizations, helping
            them modernize legacy operations, embed artificial intelligence
            into core workflows, and scale effortlessly with cloud-native
            platforms.
            Unlike traditional dev shops, we don't just write code; we engineer
            intelligent systems designed to create measurable business value
            -automating the routine to unlock human potential.
          </p>
        </div>

        {/* Stats Row - Floating Stats with animation */}
        <div className={`grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto mb-20 transition-all duration-700 delay-600 transform ${isVisible.heading ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}`}>
          {stats.map((stat, idx) => (
            <div key={idx} className="bg-gradient-to-br from-gray-900/50 to-gray-900/30 backdrop-blur-sm border border-gray-800 rounded-2xl p-5 text-center hover:border-emerald-500/30 transition-all duration-300 hover:scale-105 group">
              <div className="flex items-center justify-center gap-2 text-emerald-400 mb-3">
                {stat.icon}
                <span className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-emerald-400 to-cyan-400 bg-clip-text text-transparent">{stat.value}</span>
              </div>
              <p className="text-gray-400 text-sm font-medium">{stat.label}</p>
              <div className="w-0 group-hover:w-full h-px bg-gradient-to-r from-emerald-500 to-transparent mx-auto transition-all duration-500 mt-3"></div>
            </div>
          ))}
        </div>

        {/* Three Problem Cards Grid with staggered animations */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 w-full max-w-6xl mx-auto mb-20">
          {problemCards.map((card, index) => {
            const remainder = problemCards.length % 3;
            const isLastRow =
              remainder !== 0 &&
              index >= problemCards.length - remainder;

            return (
              <div
                key={index}
                ref={(el) => (cardsRef.current[index] = el)}
                className={`group transition-all duration-700 delay-${100 + index * 150} transform ${isVisible.cards.includes(index)
                  ? "translate-y-0 opacity-100"
                  : "translate-y-16 opacity-0"
                  } ${isLastRow ? "md:col-span-1 md:mx-auto" : ""}`}
              >
                <div className="bg-gradient-to-br from-gray-900/90 to-gray-950/90 backdrop-blur-sm border border-gray-800 rounded-2xl p-8 hover:border-emerald-500/40 hover:shadow-2xl hover:shadow-emerald-500/10 transition-all duration-500 h-full relative overflow-hidden">

                  {/* Animated gradient overlay on hover */}
                  <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 bg-gradient-to-br from-emerald-500/5 to-cyan-500/5 pointer-events-none"></div>

                  {/* Icon */}
                  <div className={`${card.iconBg} border border-gray-800 group-hover:border-emerald-500/50 rounded-xl p-4 mb-6 inline-flex items-center justify-center transition-all duration-300 group-hover:scale-110 group-hover:rotate-3`}>
                    <div className="text-emerald-400 group-hover:text-emerald-300 transition-colors duration-300">
                      {card.icon}
                    </div>
                  </div>

                  {/* Title */}
                  <h3 className="text-xl font-bold mb-3 tracking-tight group-hover:text-white transition-colors duration-300">
                    {card.title}
                  </h3>

                  {/* Description */}
                  <p className="text-gray-400 text-sm leading-relaxed group-hover:text-gray-300 transition-colors duration-300">
                    {card.description}
                  </p>

                  {/* bottom line */}
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-emerald-500 to-cyan-500 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left"></div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Enhanced Bottom Highlight Box with animation */}
        <div
          ref={bottomRef}
          className={`transition-all duration-700 delay-300 transform ${isVisible.bottomBox ? 'translate-y-0 opacity-100' : 'translate-y-12 opacity-0'}`}
        >
          <div className="w-full max-w-6xl mx-auto relative group">
            {/* Animated gradient border */}
            <div className="absolute -inset-0.5 bg-gradient-to-r from-emerald-500 via-cyan-500 to-emerald-500 rounded-2xl opacity-0 group-hover:opacity-50 blur transition duration-500"></div>

            <div className="relative bg-gradient-to-br from-gray-900 to-gray-950 border border-gray-800 rounded-2xl p-8 md:p-10 group-hover:border-emerald-500/30 transition-all duration-500">
              {/* Decorative elements */}
              <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-500/5 rounded-full blur-2xl"></div>
              <div className="absolute bottom-0 left-0 w-40 h-40 bg-cyan-500/5 rounded-full blur-2xl"></div>

              <div className="flex flex-col md:flex-row gap-8 relative z-10">
                {/* Animated Green Accent Bar */}
                <div className="relative">
                  <div className="w-1 h-16 bg-gradient-to-b from-emerald-400 to-cyan-400 rounded-full mx-auto md:mx-0"></div>
                  <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-1 h-full bg-gradient-to-b from-emerald-400/20 to-transparent animate-pulse"></div>
                </div>

                <div className="flex-1">
                  {/* Sub-label with animation */}
                  <div className="inline-flex items-center gap-2 bg-emerald-500/10 px-3 py-1 rounded-full mb-5">
                    <div className="w-1.5 h-1.5 bg-emerald-400 rounded-full animate-pulse"></div>
                    <p className="text-emerald-400 text-lg font-semibold tracking-[3px] uppercase">
                      How strong is your software strategy?
                    </p>
                  </div>

                  {/* Main text with highlighted words */}
                  <p className="text-lg md:text-xl text-gray-300 leading-relaxed font-medium">
                    For growing businesses, the challenge isn't building software. It's the absence of a
                    <span className="relative inline-block mx-1 group/highlight">
                      <span className="text-emerald-400 font-bold bg-gradient-to-r from-emerald-400 to-cyan-400 bg-clip-text text-transparent">
                        strategic, scalable development approach
                      </span>
                      <svg className="absolute -bottom-1 left-0 w-full h-3" viewBox="0 0 200 8" fill="none">
                        <path d="M0 6 L200 6" stroke="url(#waveGradient)" strokeWidth="1.5" strokeDasharray="4 4" />
                      </svg>
                    </span>
                    that delivers reliable, high-performing digital products. Poor architecture, slow development cycles, and inconsistent quality create delays, increased costs, and missed opportunities.
                    <span className="text-white font-semibold"> We help you fix that - for good.</span>
                  </p>

                  <defs>
                    <linearGradient id="waveGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                      <stop offset="0%" stopColor="#10b981" />
                      <stop offset="100%" stopColor="#06b6d4" />
                    </linearGradient>
                  </defs>

                  {/* CTA Button with animation */}
                  <div className="mt-8 flex flex-wrap gap-4">
                    <button className="group/btn relative inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-emerald-500 to-cyan-500 rounded-full font-semibold text-black hover:shadow-lg hover:shadow-emerald-500/25 transition-all duration-300 hover:scale-105">
                      <span>Start Your Software Assessment</span>
                      <FiArrowRight className="group-hover/btn:translate-x-1 transition-transform duration-300" />
                    </button>
                    <button className="relative inline-flex items-center gap-2 px-6 py-3 border border-gray-700 rounded-full font-semibold text-gray-300 hover:border-emerald-500 hover:text-emerald-400 transition-all duration-300 hover:scale-105">
                      View Our Projects
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Additional floating decorative element */}
        <div className="fixed bottom-8 right-8 pointer-events-none z-50 opacity-20 md:opacity-40">
          <div className="relative">
            <div className="w-20 h-20 border border-emerald-500/30 rounded-full animate-ping"></div>
            <div className="absolute inset-0 flex items-center justify-center">
              <FiZap className="text-emerald-500 w-8 h-8" />
            </div>
          </div>
        </div>
      </div>

      {/* Custom CSS for Tailwind delays (since tailwind CDN doesn't have all delay classes) */}
      <style jsx>{`
        .delay-100 { transition-delay: 100ms; }
        .delay-150 { transition-delay: 150ms; }
        .delay-200 { transition-delay: 200ms; }
        .delay-250 { transition-delay: 250ms; }
        .delay-300 { transition-delay: 300ms; }
        .delay-400 { transition-delay: 400ms; }
        .delay-500 { transition-delay: 500ms; }
        .delay-600 { transition-delay: 600ms; }
        .delay-700 { transition-delay: 700ms; }
        .delay-800 { transition-delay: 800ms; }
        .delay-1000 { transition-delay: 1000ms; }
        
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
        .animate-pulse {
          animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }
        .delay-1000 {
          animation-delay: 1s;
        }
      `}</style>
    </section >
  );
};

export default DarkProblemSection;